import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiGet, apiPost } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'
import { useToasts } from './Toasts'
import { usePerms } from '../auth/PermsContext'
import { useI18n } from '../i18n'

type Job = {
  id: number
  tenant_id: number
  endpoint_id: number
  task_type: string
  state: string
  created_at: string
  started_at?: string
  finished_at?: string
  payload?: any
  result?: any
  backup_mode?: string | null
  parent_job_id?: number | null
  parent_snapshot?: string | null
  snapshot_id?: string | null
  chain_id?: number | null
}
type Endpoint = { id: number; name: string }
type RepoEntry = { id: number; name: string; type: string; quarantined?: boolean }
type TargetEntry = { id: number; name: string; type: string }

export default function Jobs() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id!
  const { push } = useToasts()
  const { has } = usePerms()
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [endpointId, setEndpointId] = useState<number>(0)
  const [repositories, setRepositories] = useState<RepoEntry[]>([])
  const [repositoryId, setRepositoryId] = useState<number>(0)
  const [targets, setTargets] = useState<TargetEntry[]>([])
  const [targetId, setTargetId] = useState<number>(0)
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string|undefined>()
  const [note, setNote] = useState('manual')
  const [manualMode, setManualMode] = useState<'policy' | 'full' | 'differential' | 'incremental' | 'log'>('policy')
  const [stateFilter, setStateFilter] = useState<string>('')
  const [allowQuarantine, setAllowQuarantine] = useState(false)
  const [search, setSearch] = useState<string>('')
  const [taskType, setTaskType] = useState<string>('')
  const [since, setSince] = useState<string>('')
  const [until, setUntil] = useState<string>('')
  const [page, setPage] = useState(0)
  const pageSize = 50

  const selectedRepo = useMemo(() => repositories.find(r => r.id === repositoryId), [repositories, repositoryId])
  const loadJobs = async (reset = false) => {
    setLoading(true)
    try {
      let list: Job[] = []
      if (tenantId) {
        const params = new URLSearchParams()
        if (stateFilter) params.set('state', stateFilter)
        if (endpointId) params.set('endpoint_id', String(endpointId))
        if (search) params.set('search', search)
        if (taskType) params.set('task_type', taskType)
        if (since) params.set('since', new Date(since).toISOString())
        if (until) params.set('until', new Date(until).toISOString())
        params.set('limit', String(pageSize))
        params.set('offset', String(page * pageSize))
        list = await apiGet(`/tenants/${tenantId}/jobs?` + params.toString())
      } else {
        list = await apiGet('/jobs')
      }
      setJobs(prev => (reset ? list : [...prev, ...list]))
    } catch (e: any) {
      setError(e?.message || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { setPage(0); loadJobs(true) }, [tenantId, stateFilter, endpointId, search, taskType, since, until])

  useEffect(() => {
    if (!tenantId) {
      setEndpoints([])
      setEndpointId(0)
      return
    }
    (async () => {
      try {
        const eps = await apiGet(`/tenants/${tenantId}/endpoints`)
        setEndpoints(eps)
        if (!endpointId && eps.length) {
          setEndpointId(eps[0].id)
        }
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load endpoints', kind: 'error' })
      }
    })()
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) {
      setRepositories([])
      setRepositoryId(0)
      return
    }
    (async () => {
      try {
        const repos = await apiGet(`/tenants/${tenantId}/repositories`)
        setRepositories(repos)
        if (!repositoryId && repos.length) {
          setRepositoryId(repos[0].id)
        }
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load repositories', kind: 'error' })
      }
    })()
  }, [tenantId])

  useEffect(() => { setAllowQuarantine(false) }, [repositoryId])

  useEffect(() => {
    if (!endpointId) {
      setTargets([])
      setTargetId(0)
      return
    }
    (async () => {
      try {
        const data = await apiGet(`/endpoints/${endpointId}/targets`)
        setTargets(data)
        if (data.length && !data.find((t: TargetEntry) => t.id === targetId)) {
          setTargetId(data[0].id)
        }
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load targets', kind: 'error' })
      }
    })()
  }, [endpointId])

  const runBackup = async (e: FormEvent) => {
    e.preventDefault()
    try {
      if (!endpointId) { push({ text: t('jobs.errors.endpoint_required'), kind: 'error' }); return }
      if (!repositoryId) { push({ text: t('jobs.errors.repository_required'), kind: 'error' }); return }
      if (!targetId) { push({ text: t('jobs.errors.target_required'), kind: 'error' }); return }
      if (selectedRepo?.quarantined && !allowQuarantine) { push({ text: t('jobs.errors.repo_quarantined'), kind: 'error' }); return }
      const body: any = {
        endpoint_id: endpointId,
        task_type: 'backup',
        repository_id: repositoryId,
        target_id: targetId,
        payload: { note }
      }
      if (allowQuarantine) body.allow_quarantined = true
      if (manualMode !== 'policy') body.backup_mode = manualMode
      await apiPost('/jobs/run', body)
      await loadJobs(true)
      push({ text: t('jobs.feedback.enqueued'), kind: 'success' })
    } catch (e: any) { push({ text: e?.message || t('jobs.errors.enqueue_failed'), kind: 'error' }) }
  }

  const exportUrl = useMemo(() => {
    if (!tenantId) return '#'
    const p = new URLSearchParams()
    if (stateFilter) p.set('state', stateFilter)
    if (endpointId) p.set('endpoint_id', String(endpointId))
    if (search) p.set('search', search)
    if (taskType) p.set('task_type', taskType)
    if (since) p.set('since', new Date(since).toISOString())
    if (until) p.set('until', new Date(until).toISOString())
    p.set('limit', String(5000))
    return `/tenants/${tenantId}/jobs/export?` + p.toString()
  }, [tenantId, stateFilter, endpointId, search, taskType, since, until])

  const exportCsv = () => {
    if (tenantId) {
      window.location.href = exportUrl
      return
    }
    const rows = [
      ['id','endpoint_id','task_type','state','created_at'] as const,
      ...jobs.map(j => [String(j.id), String((j as any).endpoint_id ?? ''), j.task_type, j.state, new Date(j.created_at).toISOString()])
    ]
    const csv = rows.map(r => r.map(v => /[",\n]/.test(v) ? '"'+v.replace(/"/g,'""')+'"' : v).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'jobs.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <h2>{t('titles.jobs')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('help.jobs')}</p>
      <form onSubmit={runBackup} className="mb-3 flex flex-wrap gap-2 items-center">
        <select className="input" value={endpointId} onChange={e => setEndpointId(Number(e.target.value))} disabled={!has('jobs.create') || !endpoints.length}>
          <option value={0}>{t('jobs.placeholder.endpoint')}</option>
          {endpoints.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select className="input" value={repositoryId} onChange={e => setRepositoryId(Number(e.target.value))} disabled={!has('jobs.create') || !repositories.length}>
          <option value={0}>{t('jobs.placeholder.repository')}</option>
          {repositories.map(r => <option key={r.id} value={r.id}>{r.name} ({r.type})</option>)}
        </select>
        <select className="input" value={targetId} onChange={e => setTargetId(Number(e.target.value))} disabled={!has('jobs.create') || !targets.length}>
          <option value={0}>{t('jobs.placeholder.target')}</option>
          {targets.map(tg => <option key={tg.id} value={tg.id}>{tg.name} ({tg.type})</option>)}
        </select>
        {selectedRepo?.quarantined && (
          <div className="w-full rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-500 dark:bg-amber-500/10 dark:text-amber-200">
            <p className="font-medium">{t('jobs.hint.repo_quarantined')}</p>
            <label className="mt-2 flex items-center gap-2">
              <input type="checkbox" className="h-4 w-4" checked={allowQuarantine} onChange={e => setAllowQuarantine(e.target.checked)} />
              <span>{t('jobs.fields.allow_quarantined')}</span>
            </label>
          </div>
        )}
        <select className="input" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
          <option value="">all</option>
          <option value="queued">queued</option>
          <option value="running">running</option>
          <option value="success">success</option>
          <option value="failed">failed</option>
        </select>
        <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('ph.search')} />
        <input className="input" value={taskType} onChange={e => setTaskType(e.target.value)} placeholder={t('ph.task_type')} />
        <input className="input" type="date" value={since} onChange={e => setSince(e.target.value)} title={t('ph.from')} />
        <input className="input" type="date" value={until} onChange={e => setUntil(e.target.value)} title={t('ph.to')} />
        <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder={t('ph.note')} disabled={!has('jobs.create')} />
        <select
          className="input"
          value={manualMode}
          onChange={e => setManualMode(e.target.value as typeof manualMode)}
          disabled={!has('jobs.create')}
          title={t('jobs.fields.backup_mode') || 'Backup mode'}
        >
          <option value="policy">{t('jobs.mode.policy') || 'Policy'}</option>
          <option value="full">{t('jobs.mode.full') || 'Full'}</option>
          <option value="differential">{t('jobs.mode.differential') || 'Differential'}</option>
          <option value="incremental">{t('jobs.mode.incremental') || 'Incremental'}</option>
          <option value="log">{t('jobs.mode.log') || 'Log'}</option>
        </select>
        <button type="submit" className="btn" disabled={!has('jobs.create') || !endpoints.length}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M5 20h14v-2H5v2ZM5 4h6v5H5V4Zm8 0h6v8h-6V4ZM5 11h6v5H5v-5Z"/></svg>
          {t('buttons.run_backup')}
        </button>
        <button type="button" className="btn-secondary" onClick={exportCsv} disabled={!jobs.length && !tenantId}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2" aria-hidden="true"><path d="M5 20h14v-2H5v2ZM12 2 6.5 7.5 8 9l3-3v8h2V6l3 3 1.5-1.5L12 2Z"/></svg>
          {t('buttons.export_csv')}
        </button>
      </form>
      {!has('jobs.create') && <p className="text-xs text-gray-500 mb-2">You don't have permission to run jobs.</p>}
      {loading ? <p>Loading.</p> : error ? <p style={{ color: 'crimson' }}>{error}</p> : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                <th className="text-left">{t('table.id')}</th>
                <th className="text-left">{t('table.type')}</th>
                <th>{t('jobs.fields.mode') || 'Mode'}</th>
                <th>{t('table.state')}</th>
                <th>{t('jobs.fields.snapshot') || 'Snapshot'}</th>
                <th>{t('jobs.fields.chain') || 'Chain'}</th>
                <th>{t('table.created')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-t border-gray-200 dark:border-gray-700">
                  <td>{j.id}</td>
                  <td>{j.task_type}</td>
                  <td className="text-center">{(j.backup_mode || '').toUpperCase() || 'FULL'}</td>
                  <td className="text-center">
                    <span className={(() => {
                      const base = 'px-2 py-0.5 rounded text-xs font-medium'
                      switch (j.state) {
                        case 'success': return base + ' bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        case 'failed': return base + ' bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                        case 'running': return base + ' bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                        case 'queued': return base + ' bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        default: return base + ' bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                      }
                    })()}>{j.state}</span>
                  </td>
                  <td className="text-center text-xs">{j.snapshot_id || '—'}</td>
                  <td className="text-center text-xs">
                    {j.chain_id ? <Link className="link" to={`/jobs/${j.id}`}>#{j.chain_id}</Link> : '—'}
                  </td>
                  <td className="text-center">{new Date(j.created_at).toLocaleString()}</td>
                  <td className="text-right"><Link className="link" to={`/jobs/${j.id}`}>View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-right">
            <button className="btn-secondary" onClick={() => { const next = page + 1; setPage(next); loadJobs() }}>Load more</button>
          </div>
        </>
      )}
    </div>
  )
}
